import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';

// A guided run through the app for someone's first day. It spotlights a real part of the screen
// and says what it is for, rather than describing the Hub in the abstract — and it navigates as it
// goes, so by the end they have actually seen each page.
//
// Every step names a target by CSS selector. A step whose target isn't on the screen is skipped
// rather than pointed at nothing, which is what keeps this honest as the app changes: a page a
// particular person can't reach simply doesn't come up.

const STEPS = [
  {
    target: '.sidebar-brand',
    title: 'Welcome to the Hub',
    body: 'This is where ZG Books runs its work. Two minutes and you’ll know your way around — you can stop any time and pick it up again from the menu at the bottom left.',
    place: 'right',
  },
  {
    target: 'a[href="/my-tasks"]',
    to: '/my-tasks',
    title: 'My Tasks',
    body: 'Everything assigned to you, across every client, in one list. Most days this is the only page you need. Tick things off here and they update wherever else they appear.',
  },
  {
    target: 'a[href="/"]',
    to: '/',
    title: 'Work',
    body: 'Workflows are the jobs themselves — a month end, a BAS, a year end. Each one holds a checklist of tasks. Open one to see its tasks, notes, documents and time.',
  },
  {
    target: '.work-filters, .work-list, .kpi-card',
    title: 'Only your work shows here',
    body: 'You see a workflow when you’re named on it as its preparer or reviewer. If this page looks empty, it isn’t broken — you haven’t been put on anything yet. Ask Zevi to add you.',
    optional: true,
  },
  {
    target: '.signoff, .work-list, .work-row',
    title: 'Preparer and reviewer',
    body: 'Every workflow has someone who does the work and someone who checks it. When you’ve finished, sign off as preparer. The reviewer then signs off separately — they can’t until you have.',
    optional: true,
  },
  {
    target: '.signoff-notes, .work-list, .work-row',
    title: 'Review notes',
    body: 'A reviewer raises points back to the preparer rather than silently fixing them. Answer a note and mark it cleared. A review can’t be signed off while any note is still open.',
    optional: true,
  },
  {
    target: 'a[href="/planner"]',
    to: '/planner',
    title: 'Planner',
    body: 'Your week. Drag a workflow onto a day to block out time for it — the block is sized from its budgeted time. Connect your own Google calendar from Triage and your diary shows alongside it, meetings you’ve been invited to included.',
  },
  {
    target: 'a[href="/triage"]',
    to: '/triage',
    title: 'Triage',
    body: 'Email and notifications in one place. You see your own mailbox once you connect it, plus anything Zevi passes to you from the practice inbox. An email can become a task in one click.',
  },
  {
    target: 'a[href="/requests"]',
    to: '/requests',
    title: 'Requests',
    body: 'What we’re waiting on clients for. Chase from here, see what’s come back, and approve it. Anything a client has answered lands in “to review”.',
  },
  {
    target: 'a[href="/payroll"]',
    to: '/payroll',
    title: 'Payroll',
    body: 'Every pay run coming up, with its steps: timesheet in, pay run posted, super sent. It checks itself against Xero where it can, and ticks the steps it can prove.',
  },
  {
    target: 'a[href="/reconciliation"]',
    to: '/reconciliation',
    title: 'Uncoded',
    body: 'Transactions the client still needs to explain. Send the list, and their answers come back against each line — no spreadsheet going back and forth.',
  },
  {
    target: 'a[href="/assistant"]',
    to: '/assistant',
    title: 'Ask the Hub',
    body: 'A plain question about a client — “what did we agree about their car expenses?” — answered from that client’s notes and workflows. It only reads what you can already see.',
  },
  {
    target: 'a[href="/clients"]',
    to: '/clients',
    title: 'Clients',
    body: 'Everything on file for a client: contacts, bank accounts, properties, notes and their whole history of work. Their portal login is set up from here too.',
  },
  {
    target: '.sidebar-search',
    title: 'Search anything',
    body: 'A client, a workflow, a task — start typing and it’s there. Quicker than clicking through, once you know what you’re after.',
    optional: true,
  },
  {
    target: '.timer-bar, .main-content',
    title: 'Time',
    body: 'Start a timer on a workflow — or on one task inside it — and it keeps running while you work. Forgot to start it? Add the minutes by hand afterwards.',
    optional: true,
  },
  {
    target: '.sidebar-footer',
    title: 'That’s the tour',
    body: 'You can run it again any time from here. If a page looks empty, it usually means nothing has been assigned to you yet rather than anything being wrong.',
    place: 'right',
    last: true,
  },
];

const PAD = 8;

export default function Tour({ onClose }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const cardRef = useRef(null);

  const step = STEPS[i];

  // Find this step's target. A step whose target never turns up is skipped rather than pointed at
  // an empty corner of the screen.
  const locate = useCallback(() => {
    if (!step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        setReady(true);
        return true;
      }
    }
    return false;
  }, [step]);

  useEffect(() => {
    setReady(false);
    let cancelled = false;
    if (step && step.to) navigate(step.to);
    // Give the page a moment to render before measuring; keep looking briefly in case it is slow.
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (locate()) return;
      if (++tries > 12) {
        // Never turned up: skip it rather than show a card pointing nowhere.
        if (step && step.optional) { setI(n => Math.min(n + 1, STEPS.length - 1)); return; }
        setRect(null); setReady(true);
        return;
      }
      setTimeout(tick, 120);
    };
    const t = setTimeout(tick, step && step.to ? 260 : 60);
    return () => { cancelled = true; clearTimeout(t); };
  }, [i, locate, navigate, step]);

  // Keep the ring on the target if the window moves under it.
  useEffect(() => {
    const on = () => locate();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => { window.removeEventListener('resize', on); window.removeEventListener('scroll', on, true); };
  }, [locate]);

  const finish = useCallback(async (completed) => {
    try { await api.post('/me/tour-done'); } catch { /* it can run again */ }
    onClose(completed);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') finish(false);
      if (e.key === 'ArrowRight' && i < STEPS.length - 1) setI(i + 1);
      if (e.key === 'ArrowLeft' && i > 0) setI(i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [i, finish]);

  if (!step || !ready) return null;

  // Put the card beside the target where there's room, and in the middle when there is no target.
  let cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  if (rect) {
    const W = 340, H = 210, gap = 14;
    const vw = window.innerWidth, vh = window.innerHeight;
    const right = rect.left + rect.width + gap;
    const below = rect.top + rect.height + gap;
    if (step.place === 'right' || right + W < vw) {
      cardStyle = { top: Math.max(12, Math.min(rect.top, vh - H - 12)), left: right };
    } else if (below + H < vh) {
      cardStyle = { top: below, left: Math.max(12, Math.min(rect.left, vw - W - 12)) };
    } else {
      cardStyle = { top: Math.max(12, rect.top - H - gap), left: Math.max(12, Math.min(rect.left, vw - W - 12)) };
    }
  }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Guided tour">
      {/* Four panels around the target, so the thing being explained stays lit and clickable-looking
          while everything else dims. One box with a huge shadow would dim the target too. */}
      {rect ? (
        <>
          <div className="tour-shade" style={{ top: 0, left: 0, width: '100%', height: Math.max(0, rect.top - PAD) }} />
          <div className="tour-shade" style={{ top: rect.top + rect.height + PAD, left: 0, width: '100%', bottom: 0 }} />
          <div className="tour-shade" style={{ top: Math.max(0, rect.top - PAD), left: 0, width: Math.max(0, rect.left - PAD), height: rect.height + PAD * 2 }} />
          <div className="tour-shade" style={{ top: Math.max(0, rect.top - PAD), left: rect.left + rect.width + PAD, right: 0, height: rect.height + PAD * 2 }} />
          <div className="tour-ring" style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }} />
        </>
      ) : (
        <div className="tour-shade" style={{ inset: 0 }} />
      )}

      <div className="tour-card" style={cardStyle} ref={cardRef}>
        <div className="tour-step-count">{i + 1} of {STEPS.length}</div>
        <div className="tour-title">{step.title}</div>
        <p className="tour-body">{step.body}</p>
        <div className="tour-actions">
          <button className="link-btn" onClick={() => finish(false)}>Skip the tour</button>
          <span className="tour-actions-right">
            {i > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setI(i - 1)}>Back</button>}
            {step.last
              ? <button className="btn btn-primary btn-sm" onClick={() => finish(true)}>Finish</button>
              : <button className="btn btn-primary btn-sm" onClick={() => setI(i + 1)}>Next</button>}
          </span>
        </div>
        <div className="tour-dots">
          {STEPS.map((_, n) => <span key={n} className={n === i ? 'on' : ''} />)}
        </div>
      </div>
    </div>
  );
}
